import { useState } from "react";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { RunningClock } from "@/components/timer/RunningClock";
import { TimerPanel } from "@/components/timer/TimerPanel";
import { useElapsedSeconds } from "@/components/timer/useElapsedSeconds";
import { formatDurationSeconds } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface TimerSelectProps {
    taskId: number;
    customAttributeId: number;
    /** The committed total, in minutes, as stored on the task. */
    value: number | null;
    /**
     * When the caller's own entry on *this* field started, or null. Drives both the
     * running indicator and the live part of the displayed total.
     */
    runningSince?: Date | null;
    trigger: (content: React.ReactNode) => React.ReactElement;
    align?: "start" | "end";
}

export function TimerSelect({
    taskId,
    customAttributeId,
    value,
    runningSince,
    trigger,
    align = "start",
}: TimerSelectProps) {
    const [open, setOpen] = useState(false);

    // The stored value cannot tick, so the running segment is added here. A timer field
    // is never "unset" either — with no entries at all its total is zero, not blank.
    const committedSeconds = (value ?? 0) * 60;
    const totalSeconds = useElapsedSeconds(committedSeconds, runningSince ?? null);
    const isRunning = runningSince != null;

    const content = (
        <span className="inline-flex items-center gap-1.5 tabular-nums">
            {isRunning && <RunningClock />}
            {formatDurationSeconds(totalSeconds)}
        </span>
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger render={trigger(content)} />
            {/* Every other property selector pulls the popover back over its own row with
                this same offset, in the table and the detail panel alike. */}
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align={align} className="w-full max-w-80 p-0">
                {open && <TimerPanel taskId={taskId} customAttributeId={customAttributeId} />}
            </PopoverContent>
        </Popover>
    );
}
